<template>

  <div class="d-flex flex-column h-100">

    <div class="flex-grow-0 flex-shrink-0">

      <v-row>

        <v-col>
          <o-autocomplete
            label="Location"
            v-model="formState.locationID"
            :items="locationOptions"
          />
        </v-col>

        <v-col class="text-right">

          <v-btn
            v-if="channelDefaultRateForSpotHeroIQ !== null"
            prepend-icon="$defaultRate"
            @click="openSpotHeroIQModal"
            color="spothero"
          >SpotHero IQ</v-btn>

          <v-btn
            v-if="formState.locationID"
            :to="{ name: 'rateManagementLog', params: { locationID: formState.locationID} }"
            prepend-icon="$log"
            class="ml-4"
          >Log</v-btn>

        </v-col>

      </v-row>

    </div>

    <div class="flex-grow-1 flex-shrink-0">
      <rate-calendar
        :location="location"
      />
    </div>

  </div>

</template>

<script setup lang="ts">

import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

// components
import RateCalendar from './calendar/RateCalendar.vue'
// @ts-ignore
import SpotHeroIQModal from './calendar/modals/SpotHeroIQModal.vue'

// models
import { DefaultRate } from '../../../models/DefaultRate'
import Location from '../../../models/Location'
import LocationProduct from '../../../models/LocationProduct'

// misc
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'

interface Props {
  locationID?: null | number // optionally passed in from the route path
}

const props = withDefaults(defineProps<Props>(), {
  locationID: null,
})

const { t } = useI18n()
const title = useTitle()
title.value = t('titles.rate_management')


const locationOptions = computed(() => {
  return Location.getOptions('rateManagement', 'read', null, true)
})

/**
 * Form state
 */

interface FormState {
  locationID: null | number
}

const formState = reactive<FormState>({
  locationID: props.locationID,
})

/**
 * Miscellaneous
 */

const location = computed<null | Location>(() => {
  return formState.locationID ? Location.find(formState.locationID) : null
})

// Returns the channel_default_rate for the selected location's spothero_iq LP,
// if one exists.
const channelDefaultRateForSpotHeroIQ = computed(() => {

  // make sure that a location is selected
  if (!formState.locationID) {
    return null
  }

  // find the spothero_iq LP
  const lp = LocationProduct.query()
    .where('locationID', formState.locationID)
    .whereHas('product', (query) => {
      query.where('verticalType', 'spothero_iq')
    })
    .first()

  if (!lp) {
    return null
  }

  const defaultRate = DefaultRate.query()
    .with('channelDefaultRates', (query) => {
      query.where('channel', 'spothero')
    })
    .where('locationProductID', lp.ID)
    .first()

  if (!defaultRate || defaultRate.channelDefaultRates.length === 0) {
    return null
  }

  const channelDefaultRate = defaultRate.channelDefaultRates[0]

  // If the channelRateData doesn't actually include IQ config data, then
  // rate-workers-state-machine probably failed to fetch it (possibly because
  // IQ isn't enabled for this SpotHero facility)
  if (!channelDefaultRate.channelRateData || !('iq_config' in channelDefaultRate.channelRateData)) {
    return null
  }

  return channelDefaultRate

})

function openSpotHeroIQModal() {

  openDialog(SpotHeroIQModal, {
    locationID: formState.locationID,
    channelDefaultRate: channelDefaultRateForSpotHeroIQ.value,
  })

}

</script>
